import type { AIRequest, AIResponse } from '@/types'

// ─── Secure AI proxy transport ────────────────────────────────────────────────
// All AI calls go to this app's own /api/ai Pages Function. API keys live as
// Cloudflare Pages secrets (server-side only) — never in this bundle.

async function callServer(request: AIRequest): Promise<AIResponse> {
  const res = await fetch('/api/ai', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      prompt: request.prompt,
      systemPrompt: request.systemPrompt,
      maxTokens: request.maxTokens,
    }),
  })
  if (!res.ok) throw new Error(`AI proxy error ${res.status}`)
  const data = await res.json()
  return { content: data.content ?? '', provider: data.provider }
}

export async function callAI(request: AIRequest): Promise<AIResponse> {
  return callServer(request)
}

export async function generatePassageDNASummary(dnaName: string, answers: string[], destinations: string[] = []): Promise<string> {
  const destinationClause = destinations.length > 0
    ? ` The traveller's matched destinations are: ${destinations.join(', ')}. Reference ONLY these places by name if you name any place at all. Never mention any destination not in this list.`
    : ''
  const res = await callAI({
    systemPrompt: 'You are Passage, a refined and deeply knowledgeable travel curator for experienced travellers aged 60+. Write in an elegant, unhurried tone — as if addressing someone of great taste and experience. Two sentences maximum. Address them directly.',
    prompt: `A traveller has completed our Travel DNA quiz. Their archetype is "${dnaName}". Their answers were: ${answers.join('; ')}.${destinationClause} Write a personalised 2-sentence profile of their travel character. Warm, precise, never generic.`,
    maxTokens: 150,
  })
  return res.content.trim()
}

export async function generatePassageItinerary(destination: string, dnaName: string, days: number): Promise<{ day: number; title: string; highlight: string }[]> {
  const res = await callAI({
    systemPrompt: 'You are Passage, a refined travel curator. Respond ONLY with valid JSON — no markdown, no extra text.',
    prompt: `Generate a ${days}-day itinerary for a ${dnaName} traveller visiting ${destination}. Each day should reflect deep cultural, historical or architectural significance. Return a JSON array of ${days} objects: [{"day":1,"title":"short evocative title (max 5 words)","highlight":"one sentence of the main experience (max 25 words)"}]`,
    maxTokens: 600,
  })
  try { return JSON.parse(res.content.replace(/```json|```/g, '').trim()) }
  catch { return Array.from({ length: days }, (_, i) => ({ day: i + 1, title: `Day ${i + 1}`, highlight: `A day of discovery in ${destination}.` })) }
}
